import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Star, Archive, Send, EyeOff, Loader2 } from "lucide-react";
import ListingForm from "./ListingForm";

const STATUS = {
  published: "border-emerald-500/40 text-emerald-300 bg-emerald-900/20",
  draft: "border-neutral-500/40 text-neutral-400 bg-neutral-900/40",
  archived: "border-red-500/30 text-red-300 bg-red-900/20",
};

export default function ListingsManager() {
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [l, c] = await Promise.all([api.get("/admin/marketplace/listings"), api.get("/admin/marketplace/categories")]);
    setItems(l.data); setCats(c.data);
  };
  useEffect(() => { load().catch(() => toast.error("Could not load listings")); }, []);

  const catName = (k) => cats.find((c) => c.key === k)?.name || k;
  const strip = ({ id, external, published_at, created_at, updated_at, ...rest }) => rest;

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/admin/marketplace/listings/${form.id}`, strip(form));
      else await api.post("/admin/marketplace/listings", strip(form));
      toast.success("Listing saved"); setEditing(null); load();
    } catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Save failed — check required fields"); }
  };
  const setStatus = async (l, status) => {
    try { await api.patch(`/admin/marketplace/listings/${l.id}/status`, { status }); toast.success(`${l.title} → ${status}`); load(); }
    catch { toast.error("Status update failed"); }
  };
  const toggleFeatured = async (l) => {
    try { await api.put(`/admin/marketplace/listings/${l.id}`, { ...strip(l), featured: !l.featured }); load(); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.title}" permanently? Archive keeps it instead.`)) return;
    try { await api.delete(`/admin/marketplace/listings/${l.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const move = async (l, dir) => {
    const visible = items.filter((x) => filter === "all" || x.status === filter);
    const other = visible[visible.indexOf(l) + dir];
    if (!other) return;
    const next = [...items];
    const a = next.indexOf(l), b = next.indexOf(other);
    [next[a], next[b]] = [next[b], next[a]];
    setItems(next);
    try { await api.post("/admin/marketplace/listings/reorder", { ids: next.map((x) => x.id) }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  if (!items) return <Loader2 className="animate-spin text-[#D4AF37]" />;
  const shown = items.filter((l) => filter === "all" || l.status === filter);

  return (
    <div data-testid="mkt-listings-manager">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <div className="flex gap-2 flex-wrap items-center">
          {["all", "published", "draft", "archived"].map((s) => (
            <button key={s} data-testid={`mkt-filter-${s}`} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-[10px] tracking-widest uppercase rounded border ${filter === s ? "bg-white text-black border-white" : "border-white/15 text-neutral-400"}`}>{s}</button>
          ))}
          <span className="text-xs text-neutral-500 ml-2">{shown.length} listing{shown.length === 1 ? "" : "s"}</span>
        </div>
        <button data-testid="mkt-add-listing" onClick={() => setEditing({})} className="btn-gold px-4 py-2 rounded text-xs tracking-widest uppercase inline-flex items-center gap-2">
          <Plus size={14} /> New listing
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-xs tracking-widest uppercase text-neutral-400">
            <th className="text-left py-3 w-16">Order</th><th className="text-left">Title</th><th className="text-left">Category</th>
            <th className="text-left">Price</th><th className="text-left">Status</th><th className="text-left">Destination</th><th className="text-right">Actions</th>
          </tr></thead>
          <tbody>
            {shown.map((l) => {
              return (
                <tr key={l.id} data-testid={`mkt-row-${l.id}`} className="border-b border-white/5">
                  <td className="py-3 whitespace-nowrap">
                    <button aria-label="Move up" data-testid={`mkt-up-${l.id}`} onClick={() => move(l, -1)} className="p-1 text-neutral-500 hover:text-white"><ArrowUp size={13} /></button>
                    <button aria-label="Move down" data-testid={`mkt-down-${l.id}`} onClick={() => move(l, 1)} className="p-1 text-neutral-500 hover:text-white"><ArrowDown size={13} /></button>
                  </td>
                  <td className="text-white">
                    {l.title}
                    {l.coming_soon && <span className="ml-2 text-[9px] tracking-widest uppercase text-amber-300">soon</span>}
                    {l.new_release && <span className="ml-2 text-[9px] tracking-widest uppercase text-sky-300">new</span>}
                  </td>
                  <td className="text-neutral-400">{catName(l.category)}</td>
                  <td className="text-[#D4AF37]">{l.price_label || "—"}</td>
                  <td><span className={`inline-block px-2 py-0.5 text-[9px] tracking-widest uppercase rounded border ${STATUS[l.status]}`}>{l.status}</span></td>
                  <td className="text-neutral-500 text-xs max-w-[220px] truncate" title={l.destination_url}>{l.external ? "↗ " : ""}{l.destination_url}</td>
                  <td className="text-right whitespace-nowrap">
                    <button title={l.featured ? "Unfeature" : "Feature"} data-testid={`mkt-feature-${l.id}`} onClick={() => toggleFeatured(l)} className={`p-2 ${l.featured ? "text-[#D4AF37]" : "text-neutral-600 hover:text-neutral-300"}`}><Star size={14} fill={l.featured ? "#D4AF37" : "none"} /></button>
                    {l.status !== "published" && <button title="Publish" data-testid={`mkt-publish-${l.id}`} onClick={() => setStatus(l, "published")} className="p-2 text-emerald-400 hover:text-emerald-200"><Send size={14} /></button>}
                    {l.status === "published" && <button title="Unpublish (draft)" data-testid={`mkt-unpublish-${l.id}`} onClick={() => setStatus(l, "draft")} className="p-2 text-neutral-400 hover:text-white"><EyeOff size={14} /></button>}
                    {l.status !== "archived" && <button title="Archive" data-testid={`mkt-archive-${l.id}`} onClick={() => setStatus(l, "archived")} className="p-2 text-neutral-400 hover:text-amber-300"><Archive size={14} /></button>}
                    <button title="Edit" data-testid={`mkt-edit-${l.id}`} onClick={() => setEditing(l)} className="p-2 text-neutral-400 hover:text-[#D4AF37]"><Pencil size={14} /></button>
                    <button title="Delete" data-testid={`mkt-delete-${l.id}`} onClick={() => remove(l)} className="p-2 text-neutral-400 hover:text-red-400"><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}
            {!shown.length && <tr><td colSpan={7} className="py-6 text-center text-neutral-500">No listings in this view.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <ListingForm initial={editing} categories={cats} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}
